/**
 * Horizontal swipe → next/prev tab navigation for Dash / Home.
 * Ignores gestures that start on nested horizontal scrollers, sliders, charts, etc.
 */

const PREVENT_SELECTOR = [
	'[data-prevent-tab-swipe]',
	'[data-prevent-sidepanel-swipe]',
	'[data-prevent-drawer-swipe]',
	'.tabs',
	'.main-menu-floating',
	'.main-menu-layer',
	'canvas',
	'input[type="range"]',
].join(',');

const DEFAULT_THRESHOLD_PX = 64;
const DEFAULT_RATIO = 1.35;

export type TabSwipeNavigateOptions = {
	/** Called when the gesture should advance to the next tab (swipe left). */
	onSwipeLeft: () => void;
	/** Called when the gesture should go to the previous tab (swipe right). */
	onSwipeRight: () => void;
	/** Return false to disable handling for this gesture (route, edit mode, …). */
	isEnabled?: () => boolean;
	thresholdPx?: number;
	/** Require |dx| > |dy| * ratio before treating the gesture as horizontal. */
	horizontalRatio?: number;
	/**
	 * When > 0, ignore touches that begin within this many CSS pixels of the
	 * left or right viewport edge (leaves room for iOS back/forward gestures).
	 */
	edgeClearancePx?: number | (() => number);
};

/**
 * Next tab index for a swipe. Returns `null` at the ends so first/last
 * do not wrap around.
 */
export function nextTabIndexForSwipe(from: number, direction: 1 | -1, length: number): number | null {
	const next = from + direction;
	if (next < 0 || next >= length) return null;
	return next;
}

type TouchPoint = { x: number; y: number };

const readTouch = (event: TouchEvent): TouchPoint | null => {
	const touch = event.changedTouches?.[0] ?? event.touches?.[0];
	if (!touch) return null;
	return { x: touch.clientX, y: touch.clientY };
};

/**
 * True when the gesture should stay with a nested horizontal control / scroller
 * instead of changing tabs.
 */
export function isTabSwipeBlockedTarget(target: EventTarget | null): boolean {
	if (!(target instanceof Element)) return true;
	if (target.closest(PREVENT_SELECTOR)) return true;

	let node: Element | null = target;
	while (node && node !== document.documentElement) {
		const style = window.getComputedStyle(node);
		const overflowX = style.overflowX;
		if (
			(overflowX === 'auto' || overflowX === 'scroll' || overflowX === 'overlay') &&
			node.scrollWidth > node.clientWidth + 1
		) {
			return true;
		}
		node = node.parentElement;
	}

	return false;
}

/**
 * Attach horizontal tab-swipe listeners to a scroll host (e.g. `.main-layout-scroll`).
 * Returns a disposer.
 */
export function attachTabSwipeNavigate(host: HTMLElement, options: TabSwipeNavigateOptions): () => void {
	const threshold = options.thresholdPx ?? DEFAULT_THRESHOLD_PX;
	const ratio = options.horizontalRatio ?? DEFAULT_RATIO;

	let start: TouchPoint | null = null;
	let tracking = false;
	let blocked = false;
	let settled = false;

	const reset = () => {
		start = null;
		tracking = false;
		blocked = false;
		settled = false;
	};

	const onTouchStart = (event: TouchEvent) => {
		if (event.touches.length !== 1) {
			reset();
			return;
		}
		if (options.isEnabled && !options.isEnabled()) {
			reset();
			return;
		}
		if (isTabSwipeBlockedTarget(event.target)) {
			reset();
			blocked = true;
			return;
		}

		const point = readTouch(event);
		if (!point) {
			reset();
			return;
		}

		const edgeClearance =
			typeof options.edgeClearancePx === 'function'
				? options.edgeClearancePx()
				: (options.edgeClearancePx ?? 0);
		if (
			edgeClearance > 0 &&
			(point.x <= edgeClearance || point.x >= window.innerWidth - edgeClearance)
		) {
			reset();
			blocked = true;
			return;
		}

		start = point;
		tracking = true;
		blocked = false;
		settled = false;
	};

	const onTouchMove = (event: TouchEvent) => {
		if (!tracking || blocked || !start || settled) return;
		if (event.touches.length !== 1) {
			reset();
			return;
		}

		const point = readTouch(event);
		if (!point) return;

		const dx = point.x - start.x;
		const dy = point.y - start.y;

		// Vertical-dominant → leave the gesture to scroll / pull-to-refresh.
		if (Math.abs(dy) > Math.abs(dx) * ratio) {
			reset();
			return;
		}

		if (Math.abs(dx) < threshold) return;

		settled = true;
		if (dx < 0) {
			options.onSwipeLeft();
		} else {
			options.onSwipeRight();
		}
	};

	const onTouchEnd = () => {
		reset();
	};

	host.addEventListener('touchstart', onTouchStart, { passive: true });
	host.addEventListener('touchmove', onTouchMove, { passive: true });
	host.addEventListener('touchend', onTouchEnd, { passive: true });
	host.addEventListener('touchcancel', onTouchEnd, { passive: true });

	return () => {
		host.removeEventListener('touchstart', onTouchStart);
		host.removeEventListener('touchmove', onTouchMove);
		host.removeEventListener('touchend', onTouchEnd);
		host.removeEventListener('touchcancel', onTouchEnd);
		reset();
	};
}
